import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useRef,
  FC,
} from 'react';
import { Formik, Form, FieldArray, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Modal } from '../../../components/ui/modal';
import Button from '../../../components/ui/button/Button';
import Input from '../../../components/form/input/InputField';
import type { Factura, Cliente } from '../types/index';
import useApi from '../../../hooks/useApi';
import { format, parseISO } from 'date-fns';
import { ClienteB2C, AgenciaB2B, ReservationStatus } from '../../../hooks/useApi';
import { toast } from 'react-hot-toast';
import { FacturaFormValues, FacturaItem } from '../types/index';
import { ErrorMessage } from 'formik';

// Función para formatear números para visualización
const formatDisplayNumber = (num: number): string => {
  return num.toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// ─────────────────────────────────────────────────────────────
// Tipos y constantes
// ─────────────────────────────────────────────────────────────

// Primero, definir el tipo para tipoCliente
type TipoCliente = 'individual' | 'empresa' | 'manual';

interface FacturaFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  onSubmit: (
    data: FacturaFormValues,
    helpers: FormikHelpers<FacturaFormValues>
  ) => Promise<void> | void;
  onCancel: () => void;
  factura?: Partial<FacturaFormValues>;
}

interface InvoiceFormContentProps {
  values: FacturaFormValues;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => void;
  onClose: () => void;
  factura?: Partial<FacturaFormValues>;
}

// Interfaces de la API
interface APIExcursion {
  id: number;
  name: string;
  description: string;
  priceB2CAdult: number;
  priceB2CChild: number;
  taxes: number;
  type: string;
  destination?: string;
  active: boolean;
}

interface APIReserva {
  id: number;
  clientId: number;
  excursionId: number;
  date: string;
  hotel?: string;
  pax: number;
  status: string;
  excursion: APIExcursion;
}

interface APICliente {
  id: number;
  name: string;
  email: string;
  phone: string;
  type: 'b2c' | 'b2b';
  documentoIdentidad?: string;
  rncEmpresa?: string;
}

type APIClienteType = APICliente;

// Interfaces internas del componente
interface Excursion {
  id: number;
  nombre: string;
  descripcion: string;
  precioUnitario: number;
  impuesto: number;
  tipoServicio: string;
  destino?: string;
}

interface Reserva {
  id: number;
  clienteId?: number;    // Para reservas B2C
  agenciaId?: number;    // Para reservas B2B
  excursionId: number;
  fecha: string;
  hotel?: string;
  adultos: number;
  ninios: number;
  cantidad: number;
  estado: string;
  excursion: Excursion;
  precioTotal: number;
  montoFacturado: number;
  saldoPendiente: number;
  metodoPago: string;
  notas: string;
  type: string;
}

interface ReservaItemData {
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  impuesto: number;
  subtotal: number;
  total: number;
  hotel?: string;
  fechaServicio?: string;
  tipoServicio?: string;
  destino?: string;
  costoProveedor?: number;
  comision?: number;
}

// Definir la interfaz para los datos crudos de la reserva
interface ReservaRawData {
  id: number;
  type: 'b2c' | 'b2b';
  clientId?: number;
  cliente?: number;
  agencyId?: number;
  agencia?: number;
  excursionId?: number;
  excursion: {
    id: number;
    name: string;
    description: string;
    priceB2CAdult: number;
    priceB2BAdult: number;
    priceB2CChild: number;
    priceB2BChild: number;
    taxes: number;
    type: string;
    destination?: string;
  };
  date?: string;
  fecha?: string;
  personas?: number;
  pax?: number;
  adultos?: number;
  ninios?: number;
  hotel?: string;
  estado?: string;
  status?: string;
  precioTotal?: number;
  montoFacturado?: number;
  saldoPendiente?: number;
  metodoPago?: string;
  notas?: string;
}

// Generador de número de factura temporal
const generateTempInvoiceNumber = () => {
  const number = Math.floor(Math.random() * 999999) + 1;
  return `F-${number.toString().padStart(6, '0')}`;
};

// Esquema de validación con Yup
const facturaSchema = Yup.object().shape({
  numero: Yup.string()
    .required('El número de factura es requerido')
    .matches(/^F-\d{6}$/, 'El formato debe ser F-XXXXXX'),
  titulo: Yup.string()
    .max(200, 'El título es demasiado largo'),
  fecha: Yup.date()
    .required('La fecha es requerida')
    .max(new Date(), 'La fecha no puede ser futura')
    .min(
      new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
      'La fecha no puede ser anterior a un año'
    ),
  items: Yup.array()
    .of(
      Yup.object().shape({
        descripcion: Yup.string()
          .required('La descripción es requerida')
          .min(3, 'La descripción debe tener al menos 3 caracteres')
          .max(200, 'La descripción es demasiado larga'),
        cantidad: Yup.number()
          .required('La cantidad es requerida')
          .min(1, 'La cantidad debe ser mayor a 0')
          .max(9999, 'La cantidad es demasiado alta'),
        precioUnitario: Yup.number()
          .required('El precio es requerido')
          .min(0, 'El precio debe ser mayor o igual a 0')
          .max(999999, 'El precio es demasiado alto'),
        impuesto: Yup.number()
          .required('El impuesto es requerido')
          .min(0, 'El impuesto debe ser mayor o igual a 0')
          .max(100, 'El impuesto no puede ser mayor a 100'),
        total: Yup.number()
          .required('El total es requerido')
          .min(0, 'El total debe ser mayor o igual a 0'),
      })
    )
    .min(1, 'Debe haber al menos un item'),
  subtotal: Yup.number()
    .min(0, 'El subtotal debe ser mayor o igual a 0')
    .required('El subtotal es requerido'),
  impuestos: Yup.number()
    .min(0, 'Los impuestos deben ser mayor o igual a 0')
    .required('Los impuestos son requeridos'),
  total: Yup.number()
    .min(0, 'El total debe ser mayor o igual a 0')
    .required('El total es requerido'),
  notas: Yup.string().max(500, 'Las notas son demasiado largas'),
  abono: Yup.number()
    .min(0, 'El abono no puede ser negativo')
    .max(Yup.ref('total'), 'El abono no puede ser mayor al total')
    .default(0),
  saldoPendiente: Yup.number()
    .min(0, 'El saldo pendiente no puede ser negativo')
    .required('El saldo pendiente es requerido'),
  estado: Yup.string()
    .oneOf(['pendiente', 'abonada', 'pagada'], 'Estado no válido')
    .required('El estado es requerido'),
});

// Valores iniciales
const initialValues: FacturaFormValues = {
  numero: generateTempInvoiceNumber(),
  titulo: 'Factura', // Título por defecto
  fecha: new Date().toISOString().split('T')[0], // Fecha actual como valor por defecto
  clientId: undefined,
  tipoCliente: 'individual',
  estado: 'pendiente',
  items: [{
    descripcion: '',
    cantidad: 0,
    precioUnitario: 0,
    impuesto: 0,
    subtotal: 0,
    total: 0,
    hotel: '',
    fechaServicio: '',
    tipoServicio: '',
    destino: ''
  }],
  subtotal: 0,
  impuestos: 0,
  total: 0,
  saldoPendiente: 0
};

// ─────────────────────────────────────────────────────────────
// Hook personalizado para detectar clics fuera de un elemento
// ─────────────────────────────────────────────────────────────

function useOutsideClick(
  ref: React.RefObject<HTMLElement>,
  callback: () => void
) {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ref, callback]);
}

// ─────────────────────────────────────────────────────────────
// Componente principal del formulario de factura
// ─────────────────────────────────────────────────────────────

type ClientFieldName = 'nombreCompleto' | 'documentoIdentificacion' | 'razonSocial' | 'rncEmpresa';

interface ClientFieldsProps {
  values: FacturaFormValues;
  handleClientFieldChange: (field: ClientFieldName, value: string) => void;
  handleClientFieldFocus: () => void;
  showClientDropdown: boolean;
  filteredClientes: Cliente[];
  clientDropdownRef: React.RefObject<HTMLDivElement>;
  handleClienteSelect: (cliente: Cliente) => void;
  isManualInvoice?: boolean; // New prop to identify manual invoices
}

const ClientFields: React.FC<ClientFieldsProps> = ({
  values,
  handleClientFieldChange,
  handleClientFieldFocus,
  showClientDropdown,
  filteredClientes,
  clientDropdownRef,
  handleClienteSelect,
  isManualInvoice
}) => {
  const renderClientDropdown = () => {
    // Don't show dropdown if it's not meant to be shown
    if (!showClientDropdown) return null;
    
    // If we have results, show them
    if (filteredClientes.length > 0) {
      return (
        <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg">
          <ul className="py-1">
            {filteredClientes.map((cliente) => (
              <li
                key={cliente.id}
                className="px-3 py-2 hover:bg-blue-100 dark:hover:bg-gray-700 cursor-pointer flex flex-col"
                onClick={() => handleClienteSelect(cliente)}
              >
                <span className="font-semibold text-gray-900 dark:text-gray-100">{cliente.nombre}</span>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-col">
                  {cliente.email && <span>{cliente.email}</span>}
                  {cliente.telefono && <span>{cliente.telefono}</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    
    // Show tooltip only if user has typed something but no results found
    // Check if there's input in the relevant field based on client type
    const hasInput = values.tipoCliente === 'individual' 
      ? values.nombreCompleto && values.nombreCompleto.length > 0
      : values.razonSocial && values.razonSocial.length > 0;
    
    if (hasInput) {
      return (
        <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg">
          <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm italic flex items-center">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-md p-2 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
              <span className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                No se encontraron resultados con reservas activas
              </span>
            </div>
          </div>
        </div>
      );
    }
    
    // If user hasn't typed anything, don't show anything
    return null;
  };

  if (values.tipoCliente === 'individual') {
    return (
      <>
        <div className="relative">
          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
            Nombre completo *
          </label>
          <div className="relative">
            <Field
              type="text"
              name="nombreCompleto"
              value={values.nombreCompleto}
              placeholder="Nombre y apellidos"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleClientFieldChange('nombreCompleto', e.target.value)}
              onFocus={isManualInvoice ? undefined : handleClientFieldFocus}
              className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 dark:placeholder:text-gray-400"
            />
            {renderClientDropdown()}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
            Documento de identidad
          </label>
          <div className="relative">
            <Field
              type="text"
              name="documentoIdentificacion"
              value={values.documentoIdentificacion}
              placeholder="Cédula o pasaporte"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleClientFieldChange('documentoIdentificacion', e.target.value)}
              className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 dark:placeholder:text-gray-400"
            />
          </div>
        </div>
      </>
    );
  } else if (values.tipoCliente === 'empresa') {
    return (
      <>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
            Razón social *
          </label>
          <div className="relative">
            <Field
              type="text"
              name="razonSocial"
              value={values.razonSocial}
              placeholder="Nombre de la empresa"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleClientFieldChange('razonSocial', e.target.value)}
              onFocus={handleClientFieldFocus}
              className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 dark:placeholder:text-gray-400"
            />
            {renderClientDropdown()}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
            RNC Empresa
          </label>
          <div className="relative">
            <Field
              type="text"
              name="rncEmpresa"
              value={values.rncEmpresa}
              placeholder="RNC de la empresa"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleClientFieldChange('rncEmpresa', e.target.value)}
              className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 dark:placeholder:text-gray-400"
            />
          </div>
        </div>
      </>
    );
  }
  
  return null;
};

const InvoiceFormContent: FC<InvoiceFormContentProps> = ({
  values,
  setFieldValue,
  onClose,
  factura,
}) => {
  const { getClients, getAgencies, getReservas, getExcursiones, getHotels, getAgencyCustomPrices } = useApi();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [agencias, setAgencias] = useState<Cliente[]>([]);
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [excursiones, setExcursiones] = useState<Excursion[]>([]);
  const [hotels, setHotels] = useState<string[]>([]);
  const [filteredHotels, setFilteredHotels] = useState<string[]>([]);
  const [showHotelDropdown, setShowHotelDropdown] = useState<boolean>(false);
  const hotelDropdownRef = useRef<HTMLDivElement>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [selectedReservaData, setSelectedReservaData] = useState<ReservaItemData | null>(null);
  const [selectedReservaDescription, setSelectedReservaDescription] = useState<string>('');
  const [filteredClientes, setFilteredClientes] = useState<Cliente[]>([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showReservasDropdown, setShowReservasDropdown] = useState(false);
  // Add state for custom agency prices
  const [agencyCustomPrices, setAgencyCustomPrices] = useState<any[]>([]);
  
  // Client dropdown ref for detecting clicks outside
  const clientDropdownRef = useRef<HTMLDivElement>(null);
  const reservasDropdownRef = useRef<HTMLDivElement>(null);
  const excursionDropdownRef = useRef<HTMLDivElement>(null);
  
  // Fetch custom prices for an agency
  const fetchAgencyCustomPrices = useCallback(async (agencyId: number) => {
    try {
      const customPrices = await getAgencyCustomPrices(agencyId);
      setAgencyCustomPrices(customPrices || []);
    } catch (error) {
      console.error('Error fetching custom prices for agency:', error);
      setAgencyCustomPrices([]);
    }
  }, [getAgencyCustomPrices]);
  
  // Funciones adaptadoras
  const adaptCliente = (apiCliente: APIClienteType): Cliente => ({
    id: apiCliente.id,
    nombre: apiCliente.name,
    email: apiCliente.email,
    telefono: apiCliente.phone,
    direccion: '',
    tipo: apiCliente.type === 'b2c' ? 'individual' : 'empresa',
    documentoIdentificacion: apiCliente.documentoIdentidad || '',
    rncEmpresa: apiCliente.rncEmpresa || ''
  });

  const adaptExcursion = (apiExcursion: APIExcursion): Excursion => ({
    id: apiExcursion.id,
    nombre: apiExcursion.name,
    descripcion: apiExcursion.description,
    precioUnitario: apiExcursion.priceB2CAdult,
    impuesto: apiExcursion.taxes,
    tipoServicio: apiExcursion.type,
    destino: apiExcursion.destination
  });

  // Updated to check for custom prices
  const adaptReservaToFacturaItem = (reserva: ReservaRawData): ReservaItemData => {
    // Check if there's a custom price for this excursion
    let basePrice;
    
    if (reserva.type === 'b2b' && reserva.agencyId) {
      // Try to find a custom price for this agency and excursion
      const customPrice = agencyCustomPrices.find(cp => 
        cp.excursionId === reserva.excursion.id
      );
      
      // Use the custom price if found, otherwise use the standard B2B price
      basePrice = customPrice 
        ? Number(customPrice.priceAdult)
        : Number(reserva.excursion.priceB2BAdult);
    } else {
      // For B2C reservations, use standard B2C price
      basePrice = Number(reserva.excursion.priceB2CAdult);
    }

    // Asegurar que todos los valores numéricos estén redondeados
    const precioUnitario = basePrice;
    const cantidad = Number(reserva.pax || reserva.personas || 0);
    const impuesto = basePrice * 0.21;
    
    // Calcular subtotal y total con redondeo
    const subtotal = precioUnitario * cantidad;
    const impuestoMonto = subtotal * impuesto;
    const total = subtotal + impuestoMonto;

    // Calcular costo proveedor y comisión con redondeo
    const costoProveedor = subtotal * 0.7;
    const comision = subtotal * 0.3;

    return {
      descripcion: reserva.excursion.description,
      cantidad,
      precioUnitario,
      subtotal,
      impuesto,
      total,
      hotel: reserva.hotel || '',
      fechaServicio: reserva.date || reserva.fecha || '',
      tipoServicio: reserva.excursion.type || 'tour',
      destino: reserva.excursion.destination || '',
      costoProveedor,
      comision
    };
  };

  const adaptReservaToFactura = (reserva: any, cliente: Cliente): FacturaFormValues => {
    // Ensure we have valid numbers for all calculations
    const isEmpresa = cliente.tipo === 'empresa';
    const clienteNombre = isEmpresa 
      ? cliente.nombre
      : `${cliente.nombre}`;
    
    const facturaItem = adaptReservaToFacturaItem(reserva);

    // Create base factura object
    const facturaBase = {
      numero: generateTempInvoiceNumber(),
      fecha: format(new Date(), 'yyyy-MM-dd'),
      tipoCliente: isEmpresa ? 'empresa' as const : 'individual' as const,
      estado: 'pendiente' as const,
      nombreCompleto: isEmpresa ? '' : cliente.nombre,
      documentoIdentificacion: isEmpresa ? '' : cliente.documentoIdentificacion,
      razonSocial: isEmpresa ? cliente.nombre : '',
      rncEmpresa: isEmpresa ? cliente.rncEmpresa : '',
      direccionCliente: cliente.direccion || '',
      telefonoCliente: cliente.telefono || '',
      emailCliente: cliente.email || '',
      items: [facturaItem],
      subtotal: facturaItem.subtotal || 0,
      impuestos: (facturaItem.impuesto || 0) * (facturaItem.cantidad || 0),
      total: facturaItem.total || 0,
      clienteNombre: clienteNombre
    };

    // Add client or agency ID based on type
    if (isEmpresa) {
      return {
        ...facturaBase,
        agenciaId: cliente.id
      };
    } else {
      return {
        ...facturaBase,
        clientId: cliente.id
      };
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientesData, agenciasData, reservasData, excursionesData, hotelsData] = await Promise.all([
          getClients(),
          getAgencies(),
          getReservas(),
          getExcursiones(),
          getHotels()
        ]);

        // Adaptar clientes B2C
        const clientesAdaptados = (clientesData || []).map((cliente: any) => ({
          id: cliente.id,
          nombre: cliente.name,
          email: cliente.email,
          telefono: cliente.phone,
          direccion: '',
          tipo: 'individual' as const,
          documentoIdentificacion: cliente.documentoIdentidad || '',
          rncEmpresa: '',
          type: 'b2c' as const
        }));

        // Adaptar agencias B2B
        const agenciasAdaptadas = (agenciasData || []).map((agencia: any) => ({
          id: agencia.id,
          nombre: agencia.name,
          email: agencia.email,
          telefono: agencia.phone,
          direccion: '',
          tipo: 'empresa' as const,
          documentoIdentificacion: '',
          rncEmpresa: agencia.rncEmpresa || '',
          type: 'b2b' as const
        }));

        // Crear un mapa de excursiones para búsqueda rápida
        const excursionesMap = (excursionesData || []).reduce((map: Record<string, any>, exc: any) => {
          map[exc.name] = exc;
          return map;
        }, {});

        // Adaptar reservas
        const reservasAdaptadas = (reservasData || []).map((reserva: any) => {
          const excursionData = excursionesMap[reserva.excursion];
          if (!excursionData) {
            console.warn('No se encontró la excursión:', reserva.excursion);
            return null;
          }

          // Ensure all numeric values are correctly parsed and have fallbacks
          const precioUnitarioAdulto = Number(excursionData.priceB2CAdult) || 0;
          const precioUnitarioNino = Number(excursionData.priceB2CChild) || precioUnitarioAdulto || 0;
          const impuestoExcursion = Number(excursionData.taxes) || 0;

          // Round quantities
          const adultos = Number(reserva.adultos) || 0;
          const ninios = Number(reserva.ninios) || 0;
          const totalPersonas = adultos + ninios;
          
          // Calculate subtotals with rounding at each step
          const subtotalAdultos = precioUnitarioAdulto * adultos;
          const subtotalNinos = precioUnitarioNino * ninios;
          const subtotalTotal = subtotalAdultos + subtotalNinos;
          
          // Calculate average unit price with rounding
          const precioUnitarioPromedio = totalPersonas > 0
            ? subtotalTotal / totalPersonas
            : precioUnitarioAdulto;

          // Calculate taxes and total with rounding using the excursion's tax
          const impuestos = subtotalTotal * (impuestoExcursion / 100);
          const precioTotal = subtotalTotal + impuestos;
          
          // Handle amounts and status with rounding
          const montoFacturado = Number(reserva.montoFacturado) || 0;
          const saldoPendiente = Math.max(0, precioTotal - montoFacturado);

          // Calculate costs and commissions with rounding
          const costoProveedor = precioUnitarioPromedio * 0.7;
          const comision = precioUnitarioPromedio * 0.3;

          return {
            id: reserva.id,
            clienteId: reserva.clientId,
            agenciaId: reserva.agencyId,
            excursionId: excursionData.id,
            fecha: reserva.fecha || reserva.date,
            hotel: reserva.hotel || '',
            adultos,
            ninios,
            cantidad: totalPersonas,
            estado: reserva.estado || reserva.status,
            excursion: {
              id: excursionData.id,
              nombre: excursionData.name,
              descripcion: excursionData.description || '',
              precioUnitario: precioUnitarioPromedio,
              precioUnitarioAdulto,
              precioUnitarioNino,
              impuesto: impuestoExcursion,
              tipoServicio: excursionData.type?.toLowerCase() === 'tour' ? 'Tour' : excursionData.type || 'Tour',
              destino: excursionData.destination || ''
            },
            precioTotal,
            montoFacturado,
            saldoPendiente,
            abono: montoFacturado,
            metodoPago: reserva.metodoPago || '',
            notas: reserva.notas || '',
            type: reserva.type,
            fechaServicio: reserva.fecha || reserva.date,
            impuestos: impuestos,
            subtotal: subtotalTotal,
            costoProveedor,
            comision
          };
        }).filter(Boolean);

        setClientes(clientesAdaptados);
        setAgencias(agenciasAdaptadas);
        setReservas(reservasAdaptadas);
        setExcursiones((excursionesData || []).map(adaptExcursion));
        setHotels(hotelsData || []);
      } catch (error) {
        console.error('Error cargando datos:', error);
        toast.error('Error al cargar los datos');
      } finally {
        setIsLoadingData(false);
      }
    };
    loadData();
  }, [getClients, getAgencies, getReservas, getExcursiones, getHotels, agencyCustomPrices]);

  // Estados para controlar la visibilidad de los dropdowns
  const [activeExcursionDropdownIndex, setActiveExcursionDropdownIndex] =
    useState<number | null>(null);

  // Cerrar dropdowns al hacer clic fuera
  useOutsideClick(clientDropdownRef, () => setShowClientDropdown(false));
  useOutsideClick(reservasDropdownRef, () => setShowReservasDropdown(false));
  useOutsideClick(excursionDropdownRef, () =>
    setActiveExcursionDropdownIndex(null)
  );

  // Filtrar reservas según el cliente seleccionado
  const clienteReservas = useMemo(() => {
    if (!values.clientId && !values.agenciaId) return [];
    
    // Find the selected client or agency
    const selectedClient = values.tipoCliente === 'individual' 
      ? clientes.find(c => c.id === values.clientId)
      : agencias.find(a => a.id === values.agenciaId);
    
    if (!selectedClient) return [];
    
    return reservas.filter((r) => {
      const isClientMatch = values.tipoCliente === 'individual' 
        ? r.clienteId === values.clientId
        : r.agenciaId === values.agenciaId;
      
      const isActive = r.estado !== 'cancelled';
      
      return isClientMatch && isActive;
    }).sort((a, b) => {
      // Ordenar por fecha más reciente primero
      return new Date(b.fecha).getTime() - new Date(a.fecha).getTime();
    });
  }, [values.clientId, values.agenciaId, values.tipoCliente, reservas, clientes, agencias]);

  // Estados para almacenar la reserva seleccionada y su descripción
  // Nota: Estas variables ya están declaradas anteriormente al inicio del componente
  
  // Campos a comparar en la reserva
  const reservaFields: (keyof ReservaItemData)[] = [
    'descripcion',
    'precioUnitario',
    'impuesto',
    'tipoServicio',
    'destino',
    'cantidad',
    'hotel',
    'fechaServicio',
    'costoProveedor',
    'comision',
  ];

  // Reinicia los campos de un item y, si es el primero, la vinculación con la reserva
  const resetItemData = useCallback(
    (index: number) => {
      const defaultFields = {
        descripcion: '',
        precioUnitario: 0,
        impuesto: 0,
        tipoServicio: '',
        destino: '',
        hotel: '',
        fechaServicio: '',
        costoProveedor: 0,
        comision: 0,
        cantidad: 0,
        subtotal: 0,
        total: 0
      };

      if (selectedReservaData && window.confirm('Al modificar los datos se perderá la información de la reserva. ¿Desea continuar?')) {
      Object.entries(defaultFields).forEach(([key, value]) => {
        setFieldValue(`items.${index}.${key}`, value);
      });

        setSelectedReservaDescription('');
        setSelectedReservaData(null);
        if (values.notas?.startsWith('Reserva #')) {
          setFieldValue('notas', '');
        }
        setFieldValue('abono', 0);
        setFieldValue('subtotal', 0);
        setFieldValue('impuestos', 0);
        setFieldValue('total', 0);
        setFieldValue('saldoPendiente', 0);
        setFieldValue('estado', 'pendiente');
      }
    },
    [setFieldValue, values.notas, selectedReservaData]
  );

  // Primero, definimos un tipo más específico para los campos y sus tipos permitidos
  type FacturaItemFieldTypes = {
    descripcion: string;
    cantidad: number;
    precioUnitario: number;
    impuesto: number;
    subtotal: number;
    total: number;
    hotel: string;
    fechaServicio: string;
    tipoServicio: string;
    destino: string;
    costoProveedor: number;
    comision: number;
  };

  // Creamos un tipo de utilidad para determinar si un campo es numérico
  type NumericFields = {
    [K in keyof FacturaItemFieldTypes]: FacturaItemFieldTypes[K] extends number ? K : never
  }[keyof FacturaItemFieldTypes];

  // Creamos un tipo de utilidad para determinar si un campo es string
  type StringFields = {
    [K in keyof FacturaItemFieldTypes]: FacturaItemFieldTypes[K] extends string ? K : never
  }[keyof FacturaItemFieldTypes];

  const handleItemFieldChange = (
    index: number,
    field: keyof FacturaItemFieldTypes,
    value: string | number
  ) => {
    // Si hay una reserva seleccionada, mostrar confirmación antes de modificar
    if (selectedReservaData) {
      if (!window.confirm('Al modificar los datos se perderá la información de la reserva. ¿Desea continuar?')) {
        return;
      }
      // Resetear datos de la reserva
      setSelectedReservaDescription('');
      setSelectedReservaData(null);
      if (values.notas?.startsWith('Reserva #')) {
        setFieldValue('notas', '');
      }
      setFieldValue('abono', 0);
      setFieldValue('subtotal', 0);
      setFieldValue('impuestos', 0);
      setFieldValue('total', 0);
      setFieldValue('saldoPendiente', 0);
      setFieldValue('estado', 'pendiente');
    }

    const items = [...values.items];
    const item = items[index];

    if (
      field === 'cantidad' ||
      field === 'precioUnitario' ||
      field === 'impuesto' ||
      field === 'costoProveedor' ||
      field === 'comision'
    ) {
      const numValue = Number(value) || 0;
      (item[field as NumericFields] as number) = numValue;

      // Recalcular subtotal y total del item
      const subtotal = item.cantidad * item.precioUnitario;
      // Ensure tax calculation handles 0% correctly
      const impuestoRate = item.impuesto / 100;
      const impuestoAmount = subtotal * impuestoRate;
      const total = subtotal + impuestoAmount;

      item.subtotal = subtotal;
      item.total = total;
    } else {
      // Para campos de tipo string
      (item[field as StringFields] as string) = value.toString();
    }

    setFieldValue('items', items);

    // Recalcular totales de la factura
    const totals = calculateTotals(items, values.abono || 0);
    setFieldValue('subtotal', totals.subtotal);
    setFieldValue('impuestos', totals.impuestos);
    setFieldValue('total', totals.total);
    setFieldValue('saldoPendiente', totals.saldoPendiente);
  };

  const handleNumericInputChange = (
    index: number,
    field: keyof FacturaItemFieldTypes,
    formattedValue: string
  ) => {
    // Eliminar todo excepto números, punto decimal y signo negativo
    const numericValue = formattedValue.replace(/[^0-9.-]+/g, '');
    // Convertir a número para cálculos internos
    const value = Number(numericValue) || 0; // Ensure we always have a valid number, default to 0
    
    // Actualizar el valor interno sin redondeo
    handleItemFieldChange(index, field, value);
  };

  // Resetear campos del cliente y reiniciar datos relacionados a la reserva
  const resetClientFields = (tipo: TipoCliente) => {
    if (tipo === 'individual') {
      setFieldValue('clientId', undefined);
      setFieldValue('agenciaId', undefined);
      setFieldValue('razonSocial', '');
      setFieldValue('rncEmpresa', '');
      setFilteredClientes([]);
      // Note: We don't clear nombreCompleto and documentoIdentificacion for manual invoices
      // This allows users to enter data for manual invoices
    } else if (tipo === 'empresa') {
      setFieldValue('clientId', undefined);
      setFieldValue('agenciaId', undefined);
      setFieldValue('nombreCompleto', '');
      setFieldValue('documentoIdentificacion', '');
      setFilteredClientes([]);
      // Note: We don't clear razonSocial and rncEmpresa for manual invoices
      // This allows users to enter data for manual invoices
    }
    
    // Clear data related to reservation selection
    setSelectedReservaDescription('');
    setSelectedReservaData(null);
    setShowReservasDropdown(false);
  };

  // Actualizar campos modificables por el cliente
  const handleClientFieldChange = useCallback(
    (field: ClientFieldName, value: string) => {
      setFieldValue(field, value);
      
      // If a user is entering data manually, ensure we clear any selected entity IDs
      // to properly mark this as a manual invoice
      if (value && (field === 'nombreCompleto' || field === 'razonSocial')) {
        if (values.tipoCliente === 'individual') {
          setFieldValue('clientId', undefined);
        } else if (values.tipoCliente === 'empresa') {
          setFieldValue('agenciaId', undefined);
        }
      }
      
      // Only filter clients if there's a search value
      if (value && value.length > 1) {
        let filteredResults: Cliente[] = [];
        if (values.tipoCliente === 'individual') {
          filteredResults = clientes.filter(
            (c) =>
              c.nombre.toLowerCase().includes(value.toLowerCase()) ||
              (c.documentoIdentificacion &&
                c.documentoIdentificacion.includes(value))
          );
        } else if (values.tipoCliente === 'empresa') {
          filteredResults = agencias.filter(
            (a) =>
              a.nombre.toLowerCase().includes(value.toLowerCase()) ||
              (a.rncEmpresa && a.rncEmpresa.includes(value))
          );
        }
        setFilteredClientes(filteredResults);
        setShowClientDropdown(true); // Always show dropdown when typing, even if no results
      } else {
        setFilteredClientes([]);
        setShowClientDropdown(false);
      }
    },
    [setFieldValue, values.tipoCliente, clientes, agencias]
  );

  const handleClientFieldFocus = useCallback(() => {
    // Get relevant list based on client type
    const listaAFiltrar = values.tipoCliente === 'individual' ? clientes : agencias;
    
    // Find top 3 clients/agencies with active reservations
    const clientesConReservas = listaAFiltrar.filter(cliente => {
      const reservasActivas = reservas.filter(reserva => {
        if (values.tipoCliente === 'individual') {
          return reserva.clienteId === cliente.id && reserva.estado !== 'cancelled';
        } else {
          return reserva.agenciaId === cliente.id && reserva.estado !== 'cancelled';
        }
      });
      return reservasActivas.length > 0;
    }).slice(0, 3); // Always get top 3
    
    // Update filtered list and show dropdown only if there are results
    setFilteredClientes(clientesConReservas);
    setShowClientDropdown(clientesConReservas.length > 0);
  }, [values.tipoCliente, clientes, agencias, reservas, setFilteredClientes, setShowClientDropdown]);

  // Manejo de la selección de cliente
  const handleClienteSelect = useCallback(
    async (cliente: Cliente) => {
      // Set client type based on the selected client
      setFieldValue('tipoCliente', cliente.tipo);
      
      // Set the appropriate ID based on client type
      if (cliente.tipo === 'individual') {
        setFieldValue('clientId', cliente.id);
        setFieldValue('agenciaId', undefined);
        // Clear any agency custom prices when selecting individual client
        setAgencyCustomPrices([]);
      } else {
        setFieldValue('agenciaId', cliente.id);
        setFieldValue('clientId', undefined);
        
        // Fetch custom prices for this agency
        await fetchAgencyCustomPrices(cliente.id);
      }
      
      // Set common client fields
      setFieldValue('direccionCliente', cliente.direccion || '');
      setFieldValue('telefonoCliente', cliente.telefono || '');
      setFieldValue('emailCliente', cliente.email || '');
      setFieldValue('clienteNombre', cliente.nombre);

      // Set type-specific fields
      if (cliente.tipo === 'individual') {
        setFieldValue('nombreCompleto', cliente.nombre);
        setFieldValue('documentoIdentificacion', cliente.documentoIdentificacion || '');
        setFieldValue('razonSocial', '');
        setFieldValue('rncEmpresa', '');
      } else {
        setFieldValue('nombreCompleto', '');
        setFieldValue('documentoIdentificacion', '');
        setFieldValue('razonSocial', cliente.nombre);
        setFieldValue('rncEmpresa', cliente.rncEmpresa || '');
      }

      // Check if we should show the reservations dropdown
      const hasReservations = cliente.tipo === 'individual'
        ? reservas.some(r => r.clienteId === cliente.id)
        : reservas.some(r => r.agenciaId === cliente.id);

      setShowReservasDropdown(hasReservations);
      
      // Allow time for reservations dropdown to appear after selecting client
      setTimeout(() => {
        setShowClientDropdown(false);
        if (hasReservations) {
          const reservationDropdownRef = reservasDropdownRef.current;
          if (reservationDropdownRef) {
            reservationDropdownRef.focus();
          }
        }
      }, 300);
    },
    [setFieldValue, reservas, fetchAgencyCustomPrices]
  );

  // Función auxiliar para parsear fechas
  const parsearFecha = (fechaStr: string) => {
    try {
      // Si la fecha está en formato dd/mm/yyyy
      if (fechaStr.includes('/')) {
        const [dia, mes, anio] = fechaStr.split('/');
        const fecha = new Date(Number(anio), Number(mes) - 1, Number(dia));
        if (!isNaN(fecha.getTime())) {
          return fecha;
        }
      }

      // Si la fecha está en formato yyyy-mm-dd
      if (fechaStr.includes('-')) {
        const fecha = new Date(fechaStr);
        if (!isNaN(fecha.getTime())) {
          return fecha;
        }
      }

      // Si la fecha es un timestamp
      const timestamp = Date.parse(fechaStr);
      if (!isNaN(timestamp)) {
        return new Date(timestamp);
      }

      // Si nada funciona, retornar la fecha actual
      return new Date();
    } catch {
      return new Date();
    }
  };

  // Manejo de la selección de reserva
  const handleReservaSelect = useCallback(
    (reserva: Reserva) => {
      // Clear existing items completely
      setFieldValue('items', []);
      
      const { excursion, adultos, ninios, fecha, hotel, id, montoFacturado = 0, estado } = reserva;
      
      // Check for custom pricing if this is a B2B reservation
      let precioUnitario = Number(excursion.precioUnitario);
      
      if (reserva.type === 'b2b' && reserva.agenciaId && agencyCustomPrices.length > 0) {
        // Look for a custom price for this excursion
        const customPrice = agencyCustomPrices.find(cp => cp.excursionId === excursion.id);
        
        if (customPrice) {
          // Use the custom price if available
          precioUnitario = Number(customPrice.priceAdult);
        }
      }
      
      // Calculate tax rate and pricing
      const taxRate = (excursion.impuesto || 0) / 100;
      const costoProveedor = precioUnitario * 0.7;
      const comision = precioUnitario * 0.3;
      
      // Calculate totals for the entire reservation
      const subtotalTotal = precioUnitario * (adultos + ninios);
      const impuestosTotal = subtotalTotal * taxRate;
      const total = subtotalTotal + impuestosTotal;
      const abono = Number(montoFacturado || 0);
      const saldoPendiente = total - abono;

      // Create array for line items
      const lineItems = [];
      
      // Add adults line item if there are adults
      if (adultos > 0) {
        const subtotalAdultos = precioUnitario * adultos;
        const impuestosAdultos = subtotalAdultos * taxRate;
        const totalAdultos = subtotalAdultos + impuestosAdultos;
        
        lineItems.push({
          descripcion: `${excursion.nombre} - ${adultos} adulto${adultos > 1 ? 's' : ''}`,
          cantidad: adultos,
          precioUnitario,
          impuesto: excursion.impuesto || 0,
          tipoServicio: excursion.tipoServicio,
          destino: excursion.destino || '',
          hotel: hotel || '',
          fechaServicio: format(parsearFecha(fecha), 'yyyy-MM-dd'),
          costoProveedor,
          comision,
          subtotal: subtotalAdultos,
          total: totalAdultos
        });
      }
      
      // Add children line item if there are children
      if (ninios > 0) {
        // Check for a custom child price if this is a B2B reservation
        let childPriceUnitario = precioUnitario;
        
        if (reserva.type === 'b2b' && reserva.agenciaId && agencyCustomPrices.length > 0) {
          // Look for a custom price for this excursion
          const customPrice = agencyCustomPrices.find(cp => cp.excursionId === excursion.id);
          
          if (customPrice && customPrice.priceChild) {
            // Use the custom child price if available
            childPriceUnitario = Number(customPrice.priceChild);
          }
        }
        
        const subtotalNinios = childPriceUnitario * ninios;
        const impuestosNinios = subtotalNinios * taxRate;
        const totalNinios = subtotalNinios + impuestosNinios;
        
        lineItems.push({
          descripcion: `${excursion.nombre} - ${ninios} niño${ninios > 1 ? 's' : ''}`,
          cantidad: ninios,
          precioUnitario: childPriceUnitario,
          impuesto: excursion.impuesto || 0,
          tipoServicio: excursion.tipoServicio,
          destino: excursion.destino || '',
          hotel: hotel || '',
          fechaServicio: format(parsearFecha(fecha), 'yyyy-MM-dd'),
          costoProveedor,
          comision,
          subtotal: subtotalNinios,
          total: totalNinios
        });
      }
      
      // If no adults or children specified, create a default item
      if (lineItems.length === 0) {
        lineItems.push({
          descripcion: `${excursion.nombre}`,
          cantidad: 1,
          precioUnitario,
          impuesto: excursion.impuesto || 0,
          tipoServicio: excursion.tipoServicio,
          destino: excursion.destino || '',
          hotel: hotel || '',
          fechaServicio: format(parsearFecha(fecha), 'yyyy-MM-dd'),
          costoProveedor,
          comision,
          subtotal: subtotalTotal,
          total
        });
      }

      // Store reference to first item for use elsewhere
      setSelectedReservaData(lineItems[0]);
      setSelectedReservaDescription(excursion.nombre);
      
      // Set all line items in the form
      setFieldValue('items', lineItems);

      // Generate invoice number with F- prefix if needed
      if (!values.numero || values.numero === '(Automático)') {
        const invoiceNumber = generateTempInvoiceNumber();
        setFieldValue('numero', invoiceNumber);
      }
      
      // Set invoice title to "Factura"
      setFieldValue('titulo', 'Factura');
      
      // Update financial totals
      setFieldValue('abono', abono);
      setFieldValue('subtotal', subtotalTotal);
      setFieldValue('impuestos', impuestosTotal);
      setFieldValue('total', total);
      setFieldValue('saldoPendiente', saldoPendiente);
      
      // Map reservation status to invoice status according to business rules
      let invoiceStatus: 'pendiente' | 'abonada' | 'pagada';
      if (estado === 'pagada' || estado === 'completada') {
        invoiceStatus = 'pagada';
      } else if (estado === 'pago parcial' && abono > 0) {
        invoiceStatus = 'abonada';
      } else {
        invoiceStatus = 'pendiente';
      }
      setFieldValue('estado', invoiceStatus);

      // Set notes and other fields
      setFieldValue(
        'notas',
        `Reserva #${id} - ${excursion.nombre} - Confirmada para ${format(parsearFecha(fecha), 'dd/MM/yyyy')}`
      );
      setFieldValue('entregadoPor', 'Agente de Ventas');
      setFieldValue(
        'recibidoPor',
        values.tipoCliente === 'individual'
          ? values.nombreCompleto
          : values.razonSocial
      );
      
      // Link this invoice to the reservation
      setFieldValue('reservaId', id);
      
      setShowReservasDropdown(false);
    },
    [setFieldValue, values.tipoCliente, values.nombreCompleto, values.razonSocial, values.numero, agencyCustomPrices]
  );

  // Actualizar el filtrado de clientes
  // Remove this useMemo and use setFilteredClientes in handleClientFieldChange instead
  // const filteredClientes = useMemo(() => {
  //   const searchTerm = values.tipoCliente === 'individual'
  //     ? (values.nombreCompleto || '').toLowerCase().trim()
  //     : (values.razonSocial || '').toLowerCase().trim();
  //
  //   const listaAFiltrar = values.tipoCliente === 'individual' ? clientes : agencias;
  //   return searchTerm.length > 1
  //     ? listaAFiltrar.filter(cliente => cliente.nombre.toLowerCase().includes(searchTerm))
  //     : [];
  // }, [values.nombreCompleto, values.razonSocial, values.tipoCliente, clientes, agencias]);

  // ... existing code ...
  
  // Add a helper function to determine if this is a manual invoice
  const isManualInvoice = !values.clientId && !values.agenciaId && 
    ((values.tipoCliente === 'individual' && values.nombreCompleto) || 
     (values.tipoCliente === 'empresa' && values.razonSocial));
  
  // ... existing code ...
  
  // In the JSX, add an indicator for manual invoices
  <div className="col-span-12 md:col-span-3">
    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
      Tipo de cliente *
    </label>
    <div className="relative">
      <div className="flex space-x-4">
        <div className="flex items-center">
          <input
            type="radio"
            id="tipoIndividual"
            name="tipoCliente"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            checked={values.tipoCliente === 'individual'}
            onChange={() => {
              setFieldValue('tipoCliente', 'individual');
              resetClientFields('individual');
            }}
          />
          <label htmlFor="tipoIndividual" className="ml-2 text-sm text-gray-700 dark:text-gray-200">
            Cliente B2C
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="radio"
            id="tipoEmpresa"
            name="tipoCliente"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            checked={values.tipoCliente === 'empresa'}
            onChange={() => {
              setFieldValue('tipoCliente', 'empresa');
              resetClientFields('empresa');
            }}
          />
          <label htmlFor="tipoEmpresa" className="ml-2 text-sm text-gray-700 dark:text-gray-200">
            Agencia B2B
          </label>
        </div>
      </div>
      <div className="mt-2 text-xs text-gray-500">
        {isManualInvoice ? (
          <div className="flex items-center text-amber-600">
            <span className="mr-1">✓</span> Factura manual - No vinculada a cliente en la base de datos
          </div>
        ) : (
          <span>Para crear una factura manual, escriba los datos del cliente sin seleccionar de la lista.</span>
        )}
      </div>
    </div>
  </div>

  // ... existing code ...

  // Manejo de la selección de una excursión para un item - updated to use custom prices
  const handleExcursionSelect = useCallback(
    (excursion: Excursion, index: number) => {
      let precioUnitario = excursion.precioUnitario;
      
      // If this is a B2B client, check for custom pricing
      if (values.tipoCliente === 'empresa' && values.agenciaId) {
        // Look for a custom price for this excursion
        const customPrice = agencyCustomPrices.find(cp => cp.excursionId === excursion.id);
        
        if (customPrice) {
          // Use the custom price if available
          precioUnitario = customPrice.priceAdult;
        }
      }
      
      const { impuesto, tipoServicio, destino } = excursion;
      const costoProveedor = precioUnitario * 0.7;
      const comision = precioUnitario * 0.3;
      const fieldsToUpdate = {
        descripcion: excursion.nombre,
        precioUnitario,
        impuesto,
        tipoServicio: excursion.tipoServicio,
        destino,
        cantidad: 0,
        costoProveedor,
        comision,
      };

      Object.entries(fieldsToUpdate).forEach(([field, value]) => {
        setFieldValue(`items.${index}.${field}`, value);
      });
      setActiveExcursionDropdownIndex(null);
    },
    [setFieldValue, values.tipoCliente, values.agenciaId, agencyCustomPrices]
  );

  // ─────────────────────────────────────────────────────────────
  // Lógica mejorada para el cálculo de totales y actualización de estado
  // ─────────────────────────────────────────────────────────────

  const calculateTotals = (items: FacturaItem[], abono: number = 0) => {
    // Calculate subtotal first
    const subtotal = items.reduce((sum, item) => {
      // Make sure we're using valid numbers for calculation
      const cantidad = Number(item.cantidad) || 0;
      const precioUnitario = Number(item.precioUnitario) || 0;
      return sum + (cantidad * precioUnitario);
    }, 0);

    // Calculate taxes ensuring we have valid numbers
    const impuestos = items.reduce((sum, item) => {
      const cantidad = Number(item.cantidad) || 0;
      const precioUnitario = Number(item.precioUnitario) || 0;
      const impuestoRate = Number(item.impuesto) || 0;
      const itemSubtotal = cantidad * precioUnitario;
      return sum + (itemSubtotal * (impuestoRate / 100));
    }, 0);

    // Calculate total and pending balance
    const total = subtotal + impuestos;
    const saldoPendiente = Math.max(0, total - abono);

    return {
      subtotal,
      impuestos,
      total,
      saldoPendiente
    };
  };

  useEffect(() => {
    const abono = Number(values.abono || 0);
    const total = Number(values.total || 0);
    const saldoPendiente = Math.max(0, total - abono);
    
    let estado: 'pendiente' | 'abonada' | 'pagada' = 'pendiente';
    if (total > 0 && abono >= total) {
      estado = 'pagada';
    } else if (abono > 0) {
      estado = 'abonada';
    }

    if (values.saldoPendiente !== saldoPendiente) {
      setFieldValue('saldoPendiente', saldoPendiente);
    }
    if (values.estado !== estado) {
      setFieldValue('estado', estado);
    }
  }, [values.abono, values.total, setFieldValue]);

  // Manejo del cambio en el abono
  const handleAbonoChange = useCallback((value: string) => {
    const numeroAbono = parseFloat(value) || 0;
    const total = values.total;
    
    // Si hay una reserva seleccionada, confirmar antes de modificar
    if (selectedReservaData) {
      if (!window.confirm('Al modificar el abono se perderá la vinculación con la reserva. ¿Desea continuar?')) {
        // Si el usuario cancela, restaurar el valor original del abono
        setFieldValue('abono', selectedReservaData.total);
        return;
      }
      // Resetear datos de la reserva
      setSelectedReservaDescription('');
      setSelectedReservaData(null);
      if (values.notas?.startsWith('Reserva #')) {
        setFieldValue('notas', '');
      }
      setFieldValue('estado', 'pendiente');
    }
    
    if (numeroAbono < 0) {
      alert('El abono no puede ser negativo');
      setFieldValue('abono', 0);
      return;
    }
    
    if (numeroAbono > total) {
      if (window.confirm('El abono es mayor que el total. ¿Desea ajustarlo al total de la factura?')) {
        setFieldValue('abono', total);
      } else {
        setFieldValue('abono', 0);
      }
      return;
    }
    
    setFieldValue('abono', numeroAbono);
  }, [values.total, setFieldValue, selectedReservaData]);

  // Restore these functions that were accidentally removed
  
  // Actualizar el filtrado de excursiones
  const getFilteredExcursiones = useCallback(
    (descripcion: string) => {
      const searchTerm = descripcion.toLowerCase().trim();
      
      // console.log removed

      const filtered = excursiones.filter((e) => {
        const nombreLower = e.nombre.toLowerCase();
        const descripcionLower = e.descripcion.toLowerCase();
        return nombreLower.includes(searchTerm) || descripcionLower.includes(searchTerm);
      });

      // Removed console.log
      return filtered;
    },
    [excursiones]
  );

  // Asegurarnos de que el dropdown de excursiones se muestre
  const handleExcursionInputFocus = useCallback((index: number) => {
    // Removed console.log
    if (!selectedReservaDescription) {
      setActiveExcursionDropdownIndex(index);
    }
  }, [selectedReservaDescription]);

  // Hotel field handling
  const handleHotelFieldFocus = useCallback((index: number) => {
    // Show top 5 hotels when focusing
    const topHotels = [...hotels].slice(0, 5);
    setFilteredHotels(topHotels);
    setShowHotelDropdown(topHotels.length > 0);
  }, [hotels, setFilteredHotels, setShowHotelDropdown]);

  const handleHotelFieldChange = useCallback((index: number, value: string) => {
    // Update the hotel field
    handleItemFieldChange(index, 'hotel', value);
    
    if (value && value.length > 1) {
      // Filter hotels based on input
      const filtered = hotels.filter(hotel => 
        hotel.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5); // Limit to 5 results
      
      setFilteredHotels(filtered);
      setShowHotelDropdown(true);
    } else {
      setFilteredHotels([]);
      setShowHotelDropdown(false);
    }
  }, [hotels, handleItemFieldChange, setFilteredHotels, setShowHotelDropdown]);

  const handleHotelSelect = useCallback((index: number, hotel: string) => {
    handleItemFieldChange(index, 'hotel', hotel);
    setShowHotelDropdown(false);
  }, [handleItemFieldChange, setShowHotelDropdown]);

  // Render hotel dropdown with search results
  const renderHotelDropdown = (index: number) => {
    if (!showHotelDropdown) return null;
    
    // Show results if available
    if (filteredHotels.length > 0) {
      return (
        <div 
          ref={hotelDropdownRef}
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg"
        >
          <ul className="py-1">
            {filteredHotels.map((hotel, idx) => (
              <li
                key={idx}
                className="px-3 py-2 hover:bg-blue-100 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-gray-100"
                onClick={() => handleHotelSelect(index, hotel)}
              >
                {hotel}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    
    // Show tooltip when typing but no results found
    const currentHotel = values.items[index]?.hotel;
    const hasInput = currentHotel && currentHotel.length > 0;
    
    if (hasInput) {
      return (
        <div 
          ref={hotelDropdownRef}
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg"
        >
          <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm italic flex items-center">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-md p-2 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
              <span className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                No se encontraron hoteles que coincidan
              </span>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Setup click outside detection for hotel dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (hotelDropdownRef.current && !hotelDropdownRef.current.contains(event.target as Node)) {
        setShowHotelDropdown(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hotelDropdownRef, setShowHotelDropdown]);

  return (
    <Form className="space-y-6 p-2 sm:p-4 md:p-6 max-w-[1200px] mx-auto text-gray-900 dark:text-gray-100">
      {/* Información del documento */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">
          Información del documento
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
              Número de factura *
            </label>
            <Input
              name="numero"
              placeholder="F-000001"
              className="bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100"
              value={values.numero}
              onChange={(e) => {
                // Solo permitir cambios si es una factura existente
                if (factura?.id) {
                  setFieldValue('numero', e.target.value);
                }
              }}
            />
            {!factura?.id && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Número generado automáticamente
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
              Fecha de emisión *
            </label>
            <Input 
              type="date" 
              name="fecha" 
              value={values.fecha}
              placeholder={format(new Date(), 'yyyy-MM-dd')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
              Título de la factura
            </label>
            <Input
              name="titulo"
              placeholder="Factura"
              className="text-gray-900 dark:text-gray-100"
              value={values.titulo ?? 'Factura'}
              onChange={(e) => setFieldValue('titulo', e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              El título por defecto es "Factura"
            </p>
          </div>
        </div>
      </div>

      {/* Información del cliente */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">
          Información del cliente
        </h2>
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">
            Tipo de cliente
          </label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer group">
              <Field
                type="radio"
                name="tipoCliente"
                value="individual"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue('tipoCliente', 'individual');
                  resetClientFields('individual');
                }}
                className="form-radio text-brand-600 focus:ring-brand-500 border-gray-300 dark:border-gray-600"
              />
              <span className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                Cliente individual
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer group">
              <Field
                type="radio"
                name="tipoCliente"
                value="empresa"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue('tipoCliente', 'empresa');
                  resetClientFields('empresa');
                }}
                className="form-radio text-brand-600 focus:ring-brand-500 border-gray-300 dark:border-gray-600"
              />
              <span className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                Agencia/Empresa
              </span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" key={values.tipoCliente}>
          <ClientFields
            values={values}
            handleClientFieldChange={handleClientFieldChange}
            handleClientFieldFocus={handleClientFieldFocus}
            showClientDropdown={showClientDropdown}
            filteredClientes={filteredClientes}
            clientDropdownRef={clientDropdownRef}
            handleClienteSelect={handleClienteSelect}
            isManualInvoice={values.tipoCliente === 'manual'}
          />
        </div>

        {/* Dropdown de reservas activas */}
        {((values.clientId || values.agenciaId) && clienteReservas.length > 0) && (
          <div className="mt-6">
            <div className="relative">
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                Reservas activas
              </label>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowReservasDropdown(!showReservasDropdown)}
                className="w-full justify-between"
              >
                <span>
                  {selectedReservaDescription || 'Seleccionar reserva'}
                </span>
                <svg
                  className={`w-5 h-5 transition-transform ${
                    showReservasDropdown ? 'transform rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </Button>
              {showReservasDropdown && (
                <div
                  ref={reservasDropdownRef}
                  className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
                >
                  {clienteReservas.map((reserva) => {
                    // Calculate the total price with proper number handling
                    const adultPrice = Number(reserva.excursion.precioUnitario) || 0;
                    const adultSubtotal = adultPrice * (Number(reserva.adultos) || 0);
                    // Use precioUnitario for child price as fallback since precioUnitarioNino may not exist
                    const childPrice = Number(reserva.excursion.precioUnitario) || 0;
                    const childSubtotal = childPrice * (Number(reserva.ninios) || 0);
                    const subtotal = adultSubtotal + childSubtotal;
                    const taxRate = (Number(reserva.excursion.impuesto) || 0) / 100;
                    const impuestos = subtotal * taxRate;
                    const totalPrice = subtotal + impuestos;
                    
                    return (
                      <div
                        key={reserva.id}
                        className="px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b last:border-b-0 border-gray-200 dark:border-gray-700"
                        onClick={() => handleReservaSelect(reserva)}
                      >
                        <div className="font-medium">
                          {reserva.excursion.nombre}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Fecha: {format(parsearFecha(reserva.fecha), 'dd/MM/yyyy')}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {reserva.adultos} adultos, {reserva.ninios} niños
                          {reserva.hotel && ` - Hotel: ${reserva.hotel}`}
                        </div>
                        <div className="mt-1 text-sm font-medium text-brand-600 dark:text-brand-400">
                          Total: ${formatDisplayNumber(totalPrice)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Detalles de servicios */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Detalles de servicios
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {selectedReservaData 
                ? 'No se pueden agregar más servicios cuando hay una reserva seleccionada'
                : 'Agregue los servicios a facturar'}
            </p>
          </div>
          <FieldArray name="items">
            {({ push }) => (
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  push({
                    descripcion: '',
                    cantidad: 0,
                    precioUnitario: 0,
                    costoProveedor: 0,
                    comision: 0,
                    hotel: '',
                    fechaServicio: '',
                    tipoServicio: '',
                    destino: '',
                    impuesto: 0,
                    total: 0,
                  })
                }
                disabled={selectedReservaData !== null}
                className={`${
                  selectedReservaData
                    ? 'opacity-50 cursor-not-allowed'
                    : 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-700 hover:bg-brand-100 dark:hover:bg-brand-900/30'
                } text-brand-600 dark:text-brand-400`}
              >
                + Agregar producto/servicio
              </Button>
            )}
          </FieldArray>
        </div>
        <FieldArray name="items">
          {({ remove }) => (
            <div className="space-y-6">
              {values.items.map((_, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Producto/Servicio #{index + 1}
                    </h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => remove(index)}
                      disabled={values.items.length === 1 || selectedReservaData !== null}
                      className={`${
                        values.items.length === 1 || selectedReservaData !== null
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-red-50 dark:hover:bg-red-900/20'
                      } text-red-600 dark:text-red-400 border-red-200 dark:border-red-800`}
                    >
                      Eliminar
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 relative">
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                        Descripción *
                      </label>
                      <Input
                        name={`items.${index}.descripcion`}
                        value={values.items[index]?.descripcion || ''}
                        onChange={(e) =>
                          handleItemFieldChange(
                            index,
                            'descripcion',
                            e.target.value
                          )
                        }
                        onFocus={() => handleExcursionInputFocus(index)}
                        className="bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                      />
                      {activeExcursionDropdownIndex === index &&
                        getFilteredExcursiones(
                          values.items[index]?.descripcion || ''
                        ).length > 0 && (
                          <div
                            ref={excursionDropdownRef}
                            className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
                          >
                            <div className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300">
                              Excursiones disponibles
                            </div>
                            {getFilteredExcursiones(
                              values.items[index]?.descripcion || ''
                            ).map((excursion: Excursion) => (
                              <div
                                key={excursion.id}
                                className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() =>
                                  handleExcursionSelect(excursion, index)
                                }
                              >
                                <div className="font-medium">
                                  {excursion.nombre}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  ${excursion.precioUnitario} - {excursion.tipoServicio}
                                  {excursion.destino && ` - ${excursion.destino}`}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                        Tipo de servicio
                      </label>
                      <Input
                        name={`items.${index}.tipoServicio`}
                        value={values.items[index]?.tipoServicio || ''}
                        onChange={(e) =>
                          handleItemFieldChange(
                            index,
                            'tipoServicio',
                            e.target.value
                          )
                        }
                        placeholder="Ej: Tour, Traslado"
                        className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                        Hotel
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name={`items.${index}.hotel`}
                          value={values.items[index]?.hotel || ''}
                          onChange={(e) => handleHotelFieldChange(index, e.target.value)}
                          onFocus={() => handleHotelFieldFocus(index)}
                          placeholder="Buscar hotel..."
                          className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                        {renderHotelDropdown(index)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                        Destino
                      </label>
                      <Input
                        name={`items.${index}.destino`}
                        value={values.items[index]?.destino || ''}
                        onChange={(e) =>
                          handleItemFieldChange(
                            index,
                            'destino',
                            e.target.value
                          )
                        }
                        placeholder="Ej: Samaná, Las Terrenas"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                        Fecha del servicio
                      </label>
                      <Input
                        type="date"
                        name={`items.${index}.fechaServicio`}
                        value={values.items[index]?.fechaServicio || ''}
                        onChange={(e) =>
                          handleItemFieldChange(
                            index,
                            'fechaServicio',
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                        Cantidad *
                      </label>
                      <Input
                        type="number"
                        name={`items.${index}.cantidad`}
                        value={values.items[index]?.cantidad || 0}
                        onChange={(e) =>
                          handleItemFieldChange(
                            index,
                            'cantidad',
                            Number(e.target.value)
                          )
                        }
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                        Precio unitario *
                      </label>
                      <Input
                        type="text"
                        name={`items.${index}.precioUnitario`}
                        value={formatDisplayNumber(values.items[index]?.precioUnitario || 0)}
                        onChange={(e) => {
                          handleNumericInputChange(index, 'precioUnitario', e.target.value);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                        Costo proveedor
                      </label>
                      <Input
                        type="text"
                        name={`items.${index}.costoProveedor`}
                        value={formatDisplayNumber(values.items[index]?.costoProveedor || 0)}
                        onChange={(e) => {
                          handleNumericInputChange(index, 'costoProveedor', e.target.value);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                        Comisión
                      </label>
                      <Input
                        type="text"
                        name={`items.${index}.comision`}
                        value={formatDisplayNumber(values.items[index]?.comision || 0)}
                        onChange={(e) => {
                          handleNumericInputChange(index, 'comision', e.target.value);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                        Impuesto (%) *
                      </label>
                      <Input
                        type="number"
                        name={`items.${index}.impuesto`}
                        value={values.items[index]?.impuesto || 0}
                        onChange={(e) =>
                          handleItemFieldChange(
                            index,
                            'impuesto',
                            Number(e.target.value)
                          )
                        }
                        min="0"
                        max="100"
                        step="1"
                      />
                    </div>
                    <div className="flex items-center justify-between lg:col-span-3">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                          Total
                        </label>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          ${formatDisplayNumber(values.items[index].total)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </FieldArray>
      </div>

      {/* Totales y firmas */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">
          Resumen de totales
        </h2>
        <div className="space-y-4">
          <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700">
            <span className="text-gray-700 dark:text-gray-200">Subtotal</span>
            <span className="font-medium text-gray-900 dark:text-white">
              ${formatDisplayNumber(values.subtotal)}
            </span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700">
            <span className="text-gray-700 dark:text-gray-200">Impuestos</span>
            <span className="font-medium text-gray-900 dark:text-white">
              ${formatDisplayNumber(values.impuestos)}
            </span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700">
            <span className="text-gray-700 dark:text-gray-200">Abono</span>
            <div className="w-32">
              <Input
                type="number"
                name="abono"
                value={values.abono || ''}
                onChange={(e) => handleAbonoChange(e.target.value)}
                min="0"
                step="0.01"
                placeholder="$ 0.00"
                className="text-right bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600"
              />
            </div>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700">
            <span className="text-gray-700 dark:text-gray-200">Saldo pendiente</span>
            <span className="font-medium text-gray-900 dark:text-white">
              ${formatDisplayNumber(values.saldoPendiente || 0)}
            </span>
          </div>
          <div className="flex justify-between items-center py-3">
            <span className="text-gray-700 dark:text-gray-200">Estado</span>
            <span className={`font-medium ${
              values.estado === 'pagada' 
                ? 'text-green-600 dark:text-green-400'
                : values.estado === 'abonada'
                ? 'text-yellow-600 dark:text-yellow-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {values.estado === 'pagada' 
                ? 'Pagada'
                : values.estado === 'abonada'
                ? 'Abonada'
                : 'Pendiente'}
            </span>
          </div>
          <div className="flex justify-between items-center pt-4">
            <span className="text-lg font-semibold text-gray-900 dark:text-white">
              Total a pagar
            </span>
            <span className="text-lg font-semibold text-gray-900 dark:text-white">
              ${formatDisplayNumber(values.total)}
            </span>
          </div>
        </div>
      </div>

      {/* Información adicional */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">
          Información adicional
        </h2>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
              Notas
            </label>
            <textarea
              name="notas"
              rows={4}
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 p-3 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="Condiciones, comentarios adicionales..."
            />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                Entregado por
              </label>
              <Input name="entregadoPor" placeholder="Nombre" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                Recibido por
              </label>
              <Input name="recibidoPor" placeholder="Nombre" />
            </div>
          </div>
        </div>
      </div>

      {/* Botones de acción */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="bg-brand-600 hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-600 text-white"
        >
          {factura ? 'Actualizar' : 'Crear'} Factura
        </Button>
      </div>
    </Form>
  );
};

// Change the component declaration from a function declaration to a const with an arrow function
const FacturaForm: React.FC<FacturaFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onSubmit,
  onCancel,
  factura
}) => {
  const api = useApi();
  
  // Generar un número de factura al montar el componente
  const [tempInvoiceNumber] = useState(generateTempInvoiceNumber());

  const handleSubmit = async (values: FacturaFormValues, helpers: FormikHelpers<FacturaFormValues>) => {
    try {
      // Determine if this is a manual invoice by checking if a client/agency ID is missing
      const isManualInvoice = !values.clientId && !values.agenciaId;

      // Create a base invoice object
      const facturaData: any = {
        numero: values.numero,
        titulo: values.titulo || 'Factura', // Usar 'Factura' como valor por defecto si no hay título
        fecha: format(new Date(values.fecha), 'yyyy-MM-dd'),
        clientType: values.tipoCliente, // Client type discriminator
        estado: values.estado === 'abonada' ? 'pendiente' : values.estado,
        subtotal: values.subtotal,
        impuestoTotal: values.impuestos,
        total: values.total,
        abono: values.abono || 0,
        saldoPendiente: values.saldoPendiente || 0,
        notas: values.notas || '',
        // Optional fields
        reservationId: values.reservaId || null,
        entregadoPor: values.entregadoPor || '',
        recibidoPor: values.recibidoPor || '',
        // Set manual invoice flag based on whether we have client/agency ID
        manualInvoice: isManualInvoice
      };

      // Set client information based on whether it's a manual invoice
      if (isManualInvoice) {
        // For manual invoices, store the client name
        facturaData.clienteNombre = values.tipoCliente === 'individual' 
          ? values.nombreCompleto 
          : values.razonSocial;
        facturaData.clienteNombreManual = values.tipoCliente === 'individual'
          ? values.nombreCompleto
          : values.razonSocial;
      } else {
        // For regular invoices, set the appropriate ID
        if (values.tipoCliente === 'individual') {
          facturaData.clientId = values.clientId;
        } else {
          facturaData.agencyId = values.agenciaId;
        }
      }

      // Add invoice line items
      facturaData.lineas = values.items.map((item) => ({
        descripcion: item.descripcion,
        cantidad: item.cantidad,
        precioUnitario: item.precioUnitario,
        impuesto: item.impuesto,
        subtotal: item.subtotal,
        total: item.total,
        hotel: item.hotel || '',
        fechaServicio: item.fechaServicio ? format(new Date(item.fechaServicio), 'yyyy-MM-dd') : null,
        tipoServicio: item.tipoServicio || '',
        destino: item.destino || '',
        costoProveedor: item.costoProveedor || 0,
        comision: item.comision || 0
      }));

      let responseData;
      if (values.id) {
        responseData = await api.updateFactura(values.id, facturaData);
        toast.success('Factura actualizada exitosamente');
      } else {
        responseData = await api.createFactura(facturaData);
        toast.success('Factura creada exitosamente');
      }

      // Llamamos a onSuccess después de crear/actualizar la factura
      // para actualizar la lista de facturas en FacturaList
      await onSuccess();
      onClose();
    } catch (error) {
      console.error('Error al procesar la factura:', error);
      // Check if it's an API error with a specific message
      if (error instanceof Error) {
        // Try to display the specific error message from the API
        const apiError = error as any;
        if (apiError.name === 'ApiError' && apiError.data && apiError.data.message) {
          toast.error(apiError.data.message);
        } else {
          toast.error(error.message || 'Error al procesar la factura');
        }
      } else {
        toast.error('Error al procesar la factura');
      }
      helpers.setSubmitting(false);
    }
  };

  const initialValues: FacturaFormValues = {
    numero: factura?.numero || tempInvoiceNumber, // Usar el número generado si no hay factura existente
    titulo: factura?.titulo || 'Factura', // Usar 'Factura' como valor por defecto
    fecha: factura?.fecha || format(new Date(), 'yyyy-MM-dd'), // Usar la fecha actual como valor por defecto
    clientId: factura?.clientId,
    tipoCliente: factura?.tipoCliente || 'individual',
    estado: factura?.estado || 'pendiente',
    items: factura?.items || [{
      descripcion: '',
      cantidad: 0,
      precioUnitario: 0,
      impuesto: 0,
      subtotal: 0,
      total: 0,
      hotel: '',
      fechaServicio: '',
      tipoServicio: '',
      destino: '',
      costoProveedor: 0,
      comision: 0
    }],
    subtotal: factura?.subtotal || 0,
    impuestos: factura?.impuestos || 0,
    total: factura?.total || 0,
    notas: factura?.notas || '',
    entregadoPor: factura?.entregadoPor || '',
    recibidoPor: factura?.recibidoPor || '',
    nombreCompleto: factura?.nombreCompleto || '',
    documentoIdentificacion: factura?.documentoIdentificacion || '',
    razonSocial: factura?.razonSocial || '',
    rncEmpresa: factura?.rncEmpresa || '',
    abono: factura?.abono || 0,
    saldoPendiente: factura?.saldoPendiente || 0,
    direccionCliente: factura?.direccionCliente || '',
    telefonoCliente: factura?.telefonoCliente || '',
    emailCliente: factura?.emailCliente || '',
    clienteNombre: factura?.clienteNombre || '',
    agenciaId: factura?.agenciaId || undefined,
    reservaId: factura?.reservaId || undefined
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={factura ? 'Editar factura' : 'Nueva factura'}
      size="xl"
      className="sm:max-w-[1200px] bg-gray-50 dark:bg-gray-900 w-full mx-auto h-[100dvh] sm:h-auto overflow-y-auto sm:overflow-visible"
      position="center"
      preventScroll
      closeOnEscape
      showCloseButton
    >
      <Formik<FacturaFormValues>
        initialValues={initialValues}
        validationSchema={facturaSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <InvoiceFormContent
            values={values}
            setFieldValue={setFieldValue}
            onClose={onClose}
            factura={factura}
          />
        )}
      </Formik>
    </Modal>
  );
};

// Export the component correctly
export { FacturaForm };
export default FacturaForm;

